// src/pages/VestTrades.js  (admin panel)
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { adminAPI } from '../api';

function fmt(n) { return parseFloat(n || 0).toFixed(2); }

// ── Tier Form Modal ───────────────────────────────────────────
function TierModal({ tier, onClose, onSaved }) {
  const isEdit = !!tier?.id;
  const [form, setForm] = useState({
    name:          tier?.name          || '',
    description:   tier?.description   || '',
    return_rate:   tier?.return_rate   ?? 15,
    duration_days: tier?.duration_days ?? 20,
    is_active:     tier?.is_active     ?? 1,
    sort_order:    tier?.sort_order    ?? 0,
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required.');
    if (!form.return_rate || !form.duration_days) return toast.error('Return rate and duration are required.');
    setBusy(true);
    try {
      if (isEdit) {
        await adminAPI.updateVestTier(tier.id, form);
        toast.success('Tier updated.');
      } else {
        await adminAPI.createVestTier(form);
        toast.success('Tier created.');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: 12, padding: 28,
        width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0 }}>{isEdit ? 'Edit Tier' : 'Create Tier'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
        </div>

        <div className="form-group">
          <label className="form-label">Tier Name</label>
          <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Starter" />
        </div>
        <div className="form-group">
          <label className="form-label">Description (optional)</label>
          <textarea className="form-control" rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description shown to users" />
        </div>
        <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">Return Rate (%)</label>
            <input className="form-control" type="number" min="0" step="0.01" value={form.return_rate} onChange={e => set('return_rate', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Duration (days)</label>
            <input className="form-control" type="number" min="1" value={form.duration_days} onChange={e => set('duration_days', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Sort Order</label>
            <input className="form-control" type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-control" value={form.is_active} onChange={e => set('is_active', parseInt(e.target.value))}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
        </div>

        {/* Preview */}
        <div style={{ background: '#f0fdfa', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: '#0f766e', fontWeight: 600, marginBottom: 4 }}>Preview</div>
          <div style={{ fontSize: 13, color: '#134e4a' }}>
            Invest any amount (min set in Settings) in <strong>{form.name || 'this tier'}</strong> and earn <strong>{form.return_rate}%</strong> after <strong>{form.duration_days} days</strong>.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={busy} style={{ flex: 2 }}>
            {busy ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Tier')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function VestTrades() {
  const [tab, setTab]               = useState('tiers');
  const [tiers, setTiers]           = useState([]);
  const [investments, setInvestments] = useState([]);
  const [stats, setStats]           = useState(null);
  const [settings, setSettings]     = useState([]);
  const [settingsChanges, setSettingsChanges] = useState({});
  const [savingSettings, setSavingSettings]   = useState(false);
  const [modalTier, setModalTier]   = useState(null);  // null = closed, {} = new, {id,...} = edit
  const [loading, setLoading]       = useState(true);
  const [deleting, setDeleting]     = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tiersRes, invRes, settRes] = await Promise.allSettled([
        adminAPI.vestTiers(),
        adminAPI.vestInvestments(),
        adminAPI.settings(),
      ]);
      if (tiersRes.status === 'fulfilled') setTiers(tiersRes.value.data.tiers || []);
      if (invRes.status === 'fulfilled') {
        setInvestments(invRes.value.data.investments || []);
        setStats(invRes.value.data.stats || null);
      }
      if (settRes.status === 'fulfilled') setSettings(settRes.value.data.settings || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleDelete = async (tier) => {
    if (!window.confirm(`Delete tier "${tier.name}"? This cannot be undone.`)) return;
    setDeleting(tier.id);
    try {
      await adminAPI.deleteVestTier(tier.id);
      toast.success('Tier deleted.');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete tier.');
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleActive = async (tier) => {
    try {
      await adminAPI.updateVestTier(tier.id, { is_active: tier.is_active ? 0 : 1 });
      toast.success(tier.is_active ? 'Tier deactivated.' : 'Tier activated.');
      loadAll();
    } catch (err) {
      toast.error('Update failed.');
    }
  };

  const getSettingVal = (key) => {
    if (settingsChanges[key] !== undefined) return settingsChanges[key];
    return settings.find(s => s.setting_key === key)?.setting_value || '';
  };
  const setSettingVal = (key, val) => setSettingsChanges(c => ({ ...c, [key]: val }));

  const saveSettings = async () => {
    if (!Object.keys(settingsChanges).length) return toast.error('No changes to save.');
    setSavingSettings(true);
    try {
      const updates = Object.entries(settingsChanges).map(([key, value]) => ({ key, value }));
      await adminAPI.saveSettings(updates);
      toast.success('Settings saved!');
      setSettingsChanges({});
      loadAll();
    } catch {
      toast.error('Failed to save settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const activeInvCount    = investments.filter(i => i.status === 'active').length;
  const completedInvCount = investments.filter(i => i.status === 'completed').length;

  return (
    <div>
      {modalTier !== null && (
        <TierModal
          tier={modalTier?.id ? modalTier : undefined}
          onClose={() => setModalTier(null)}
          onSaved={loadAll}
        />
      )}

      {/* Stats row */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total Investments',  val: investments.length },
            { label: 'Active Volume (GHS)', val: fmt(stats.total_active_amount) },
            { label: 'Total Paid Out (GHS)', val: fmt(stats.total_paid_out) },
          ].map(({ label, val }) => (
            <div key={label} className="section-card" style={{ textAlign: 'center', padding: '14px 10px' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--blue-mid)' }}>{val}</div>
              <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { id: 'tiers',       label: `Tiers (${tiers.length})` },
          { id: 'investments', label: `Investments${activeInvCount ? ` (${activeInvCount} active)` : ''}` },
          { id: 'settings',    label: 'Settings' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`btn ${tab === t.id ? 'btn-primary' : 'btn-ghost'}`}
            style={{ fontSize: 13 }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>Loading...</div>
      ) : tab === 'tiers' ? (
        /* ── Tiers ── */
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <button className="btn btn-primary" onClick={() => setModalTier({})}>+ New Tier</button>
          </div>

          {tiers.length === 0 ? (
            <div className="section-card" style={{ textAlign: 'center', padding: 32, color: 'var(--gray-400)' }}>
              No tiers yet. Create one above.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)' }}>
                    {['Name', 'Return Rate', 'Duration', 'Status', 'Sort', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-600)', borderBottom: '2px solid var(--gray-200)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tiers.map(tier => (
                    <tr key={tier.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: 700 }}>{tier.name}</div>
                        {tier.description && <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{tier.description}</div>}
                      </td>
                      <td style={{ padding: '12px', fontWeight: 700, color: '#0f766e' }}>{tier.return_rate}%</td>
                      <td style={{ padding: '12px' }}>{tier.duration_days} days</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                          background: tier.is_active ? '#d1e7dd' : 'var(--gray-100)',
                          color: tier.is_active ? '#0a3622' : 'var(--gray-500)',
                        }}>
                          {tier.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>{tier.sort_order}</td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => setModalTier(tier)}>
                            Edit
                          </button>
                          <button
                            className="btn btn-ghost"
                            style={{ fontSize: 12, padding: '5px 10px', color: tier.is_active ? '#856404' : '#0a3622' }}
                            onClick={() => handleToggleActive(tier)}
                          >
                            {tier.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="btn btn-ghost"
                            style={{ fontSize: 12, padding: '5px 10px', color: '#dc3545' }}
                            onClick={() => handleDelete(tier)}
                            disabled={deleting === tier.id}
                          >
                            {deleting === tier.id ? '...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : tab === 'investments' ? (
        /* ── Investments overview ── */
        <div>
          <div style={{ marginBottom: 10, fontSize: 13, color: 'var(--gray-500)' }}>
            Showing all user investments. Read-only view.
            {completedInvCount > 0 && (
              <span style={{ marginLeft: 8, color: '#856404', fontWeight: 600 }}>
                {completedInvCount} completed and awaiting user claim.
              </span>
            )}
          </div>
          {investments.length === 0 ? (
            <div className="section-card" style={{ textAlign: 'center', padding: 32, color: 'var(--gray-400)' }}>No investments yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)' }}>
                    {['User', 'Tier', 'Invested', 'Payout', 'Rate', 'Completes', 'Status'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-600)', borderBottom: '2px solid var(--gray-200)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {investments.map(inv => (
                    <tr key={inv.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 600 }}>{inv.username}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{inv.email}</div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>{inv.tier_name}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700 }}>GHS {fmt(inv.amount)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: '#0f766e' }}>GHS {fmt(inv.payout_amount)}</td>
                      <td style={{ padding: '10px 12px' }}>{inv.return_rate}%</td>
                      <td style={{ padding: '10px 12px' }}>{new Date(inv.completes_at).toLocaleDateString()}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                          background: inv.status === 'active' ? '#d1e7dd' : inv.status === 'completed' ? '#fff3cd' : '#cfe2ff',
                          color:      inv.status === 'active' ? '#0a3622' : inv.status === 'completed' ? '#856404' : '#084298',
                        }}>
                          {inv.status === 'withdrawn' ? 'Claimed' : inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* ── Settings ── */
        <div>
          <div className="section-card">
            <h3 style={{ marginTop: 0, marginBottom: 16 }}>VestTrades Settings</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Minimum Investment (GHS)</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  step="0.01"
                  value={getSettingVal('vesttrades_min_purchase')}
                  onChange={e => setSettingVal('vesttrades_min_purchase', e.target.value)}
                />
                <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                  Minimum amount users must invest per tier entry. Default: 70
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Minimum VestTrades Withdrawal (GHS)</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  step="0.01"
                  value={getSettingVal('vesttrades_min_withdrawal')}
                  onChange={e => setSettingVal('vesttrades_min_withdrawal', e.target.value)}
                />
                <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                  Minimum amount to withdraw from VestTrades balance to main account. Default: 50
                </div>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <button
                className="btn btn-primary"
                onClick={saveSettings}
                disabled={savingSettings || !Object.keys(settingsChanges).length}
              >
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>

          <div className="section-card" style={{ marginTop: 16 }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>How VestTrades Works</h3>
            <div style={{ fontSize: 13, color: 'var(--gray-600)', lineHeight: 1.8 }}>
              <div>1. You create tiers here (e.g. Starter: 15% over 20 days).</div>
              <div>2. Users add funds to a tier using their withdrawal PIN (deducted from main balance).</div>
              <div>3. Each investment runs independently for its tier duration.</div>
              <div>4. When time is up, users visit VestTrades History and click Withdraw to receive payout in their VestTrades balance.</div>
              <div>5. Users then withdraw from VestTrades balance to their main account balance (min GHS {getSettingVal('vesttrades_min_withdrawal') || '50'}).</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
